import type { EventProduct } from "../../api/specialized.ts";

/* ── Scan & Go (SNG) customer events ─────────────────────────
 * An SNG kiosk logs a SCANNED event (always Quantity 1) for every scan, and
 * at payment one WEIGHT event per cart line holding that line's quantity
 * (LoginSNG.vb). Despite the name no scale is involved: the WEIGHT rows are a
 * snapshot of the cart when the customer paid. Listed as is, three scans read
 * as six products, so the snapshot is hidden when it only repeats the scans.
 * Removing an item from an SNG cart logs nothing, so when the snapshot differs
 * from the scans it is kept and flagged CartAtPayment instead. */

/** True for a session opened by the SNG cart page (CartPageSNG.xaml.vb). */
export function isSngSession(events: EventProduct[]): boolean {
    return events.some(e => e.Event === "INITIAL" && (e.Extra ?? "").startsWith("SNG cart session"));
}

/** The cart snapshot WEIGHT rows written at payment. Rejected scans also log
 *  WEIGHT, but as invalid rows with Quantity 0. */
function isCartAtPaymentRow(e: EventProduct): boolean {
    return e.Event === "WEIGHT" && e.IsValid && e.ProductID > 0 && e.Quantity > 0;
}

function quantityByProduct(events: EventProduct[]): Map<number, number> {
    const m = new Map<number, number>();
    for (const e of events) m.set(e.ProductID, (m.get(e.ProductID) ?? 0) + e.Quantity);
    return m;
}

/** Drops the payment snapshot of an SNG session when it matches the scans
 *  product for product, otherwise marks its rows CartAtPayment. Other
 *  sessions are returned unchanged. */
export function collapseSngCartSnapshot(events: EventProduct[]): EventProduct[] {
    if (!isSngSession(events)) return events;
    const snapshot = events.filter(isCartAtPaymentRow);
    if (snapshot.length === 0) return events;

    const scanned = quantityByProduct(events.filter(e => e.Event === "SCANNED" && e.ProductID > 0));
    const paid = quantityByProduct(snapshot);
    const same = scanned.size === paid.size && [...paid].every(([id, qty]) => scanned.get(id) === qty);
    if (same) return events.filter(e => !isCartAtPaymentRow(e));

    for (const e of snapshot) e.CartAtPayment = true;
    return events;
}
